import { Modal, NumberInput } from '@mantine/core';
import type { BlockStorageQuota } from '@xver/apis/v1/block-storage/model';
import { Button, InputField } from '@xver/components/Atoms';
import { maxVolumeSize, MIN_VOLUME_GIB, volumeSizeError } from '@xver/utils/blockStorage';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    formatBaht,
    volumeGB,
    volumeMonthlyCost,
} from '../../ContainerEngineInstancePage/utils/volumeCost';
import {
    validateVolumeName,
    VOLUME_ERROR_DEFAULTS,
    type VolumeErrorKey,
} from '../../ContainerEngineInstancePage/utils/volumeValidation';
import {
    useBlockStorageErrorText,
    useBlockVolumeMutations,
} from '../hooks/useBlockVolumeMutations';
import { useBlockStorageRate } from '../hooks/useBlockStorageRate';

export const NUMBER_INPUT_STYLES = {
    input: {
        backgroundColor: 'var(--surface-2)',
        borderColor: 'var(--border)',
        color: 'var(--fg)',
    },
};

const QUICK_SIZES = [10, 50, 100, 500];
const DEFAULT_SIZE = 10;

interface CreateVolumeModalProps {
    opened: boolean;
    /** The owner's limits and usage, from `resolveQuota`. */
    quota: BlockStorageQuota;
    onClose: () => void;
}

export default function CreateVolumeModal({ opened, quota, onClose }: CreateVolumeModalProps) {
    const { t } = useTranslation(['blockStorage', 'containerEngineInstance', 'common']);
    const errorText = useBlockStorageErrorText();
    const { createVolume } = useBlockVolumeMutations();
    const rate = useBlockStorageRate();
    const nameRef = useRef<HTMLInputElement>(null);
    const sizeRef = useRef<HTMLInputElement>(null);

    const [name, setName] = useState('');
    const [sizeGiB, setSizeGiB] = useState<number>(DEFAULT_SIZE);
    const [showErrors, setShowErrors] = useState(false);

    const { reset } = createVolume;
    useEffect(() => {
        if (!opened) return;
        setName('');
        setSizeGiB(DEFAULT_SIZE);
        setShowErrors(false);
        reset();
    }, [opened, reset]);

    const message = (key: VolumeErrorKey | null) =>
        showErrors && key
            ? t(`containerEngineInstance:volumes.validation.${key}`, VOLUME_ERROR_DEFAULTS[key])
            : undefined;

    const limit = maxVolumeSize(quota);
    const quickSizes = QUICK_SIZES.filter((size) => size <= limit.max);
    const nameError = validateVolumeName(name);
    const sizeError = volumeSizeError(sizeGiB, limit);
    const isPending = createVolume.isPending;

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();
        if (isPending) return;
        if (nameError || sizeError) {
            setShowErrors(true);
            (nameError ? nameRef : sizeRef).current?.focus();
            return;
        }
        createVolume.mutate({ name: name.trim(), sizeGiB }, { onSuccess: onClose });
    };

    const handleClose = () => {
        if (!isPending) onClose();
    };

    return (
        <Modal
            size="md"
            opened={opened}
            onClose={handleClose}
            centered
            closeOnClickOutside={!isPending}
            closeOnEscape={!isPending}
            closeButtonProps={{ 'aria-label': t('common:actions.close'), disabled: isPending }}
            title={<span className="text-base font-bold">{t('create.title')}</span>}
            overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
            transitionProps={{ transition: 'fade', duration: 128, timingFunction: 'linear' }}
        >
            <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
                <p className="text-sm text-fg/68">{t('create.description')}</p>

                <InputField
                    ref={nameRef}
                    data-autofocus
                    size="md"
                    width="100%"
                    label={t('fields.name')}
                    description={t('fields.nameDescription')}
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    error={message(nameError)}
                    readOnly={isPending}
                    autoComplete="off"
                    spellCheck={false}
                />

                <div className="w-full flex flex-col gap-2">
                    <NumberInput
                        ref={sizeRef}
                        size="md"
                        label={t('fields.size')}
                        description={t(`fields.sizeLimit.${limit.binding}`, {
                            min: MIN_VOLUME_GIB,
                            max: limit.max,
                            limit: quota.limitGiB,
                        })}
                        min={MIN_VOLUME_GIB}
                        max={limit.max}
                        clampBehavior="none"
                        allowDecimal={false}
                        allowNegative={false}
                        value={sizeGiB}
                        onChange={(value) => setSizeGiB(Number(value))}
                        error={
                            showErrors && sizeError
                                ? t(`sizeErrors.${sizeError}`, {
                                      min: MIN_VOLUME_GIB,
                                      max: limit.max,
                                      used: quota.usedGiB,
                                      limit: quota.limitGiB,
                                  })
                                : undefined
                        }
                        readOnly={isPending}
                        styles={NUMBER_INPUT_STYLES}
                    />
                    {quickSizes.length > 0 && (
                        <div
                            className="flex flex-row flex-wrap items-center gap-2"
                            role="group"
                            aria-label={t('fields.quickSizes')}
                        >
                            {quickSizes.map((size) => (
                                <button
                                    key={size}
                                    type="button"
                                    aria-pressed={sizeGiB === size}
                                    onClick={() => !isPending && setSizeGiB(size)}
                                    className={`min-h-6 px-2.5 py-1 rounded text-xs font-medium border transition-colors ${
                                        sizeGiB === size
                                            ? 'border-fg/40 bg-fg/10 text-fg'
                                            : 'border-fg/15 text-fg/68 hover:bg-fg/8'
                                    }`}
                                >
                                    {t('sizeValue', { size })}
                                </button>
                            ))}
                        </div>
                    )}
                    <p className="text-sm text-fg/78">
                        {t('create.estimate', {
                            amount: formatBaht(volumeMonthlyCost(sizeGiB, rate)),
                            gb: volumeGB(sizeGiB).toFixed(2),
                        })}
                    </p>
                    <p className="text-xs text-fg/68">
                        {t('pricingNote', { rate: formatBaht(rate) })}
                    </p>
                </div>

                {createVolume.error && (
                    <p role="alert" className="text-sm text-fg">
                        {errorText(createVolume.error)}
                    </p>
                )}

                <div className="flex flex-wrap justify-end gap-2">
                    <Button variant="secondary" onClick={handleClose} disabled={isPending}>
                        {t('common:actions.cancel')}
                    </Button>
                    <Button type="submit" isLoading={isPending}>
                        {t('create.submit')}
                    </Button>
                </div>
            </form>
        </Modal>
    );
}
